import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, SHADOWS, FONTS } from '../../styles/tokens';
import {
  fetchLoginEvents, fetchAdminEvents,
} from '../../api/keycloak-admin';

type EventEntry = {
  time: number;
  type: string;
  user: string;
  ip: string;
  detail: string;
  source: 'login' | 'admin';
};

/** Audit log page — chronological event stream with filters and CSV export */
export function AuditLog(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [events, setEvents] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [loginEvts, adminEvts] = await Promise.all([
        fetchLoginEvents(token, { max: 100 }),
        fetchAdminEvents(token, { max: 100 }),
      ]);

      const entries: EventEntry[] = [];

      for (const evt of loginEvts) {
        entries.push({
          time: evt.time,
          type: evt.type,
          user: evt.details?.username ?? evt.userId?.slice(0, 8) ?? 'unknown',
          ip: evt.ipAddress ?? '',
          detail: evt.clientId ?? '',
          source: 'login',
        });
      }

      for (const evt of adminEvts) {
        entries.push({
          time: evt.time,
          type: evt.operationType,
          user: evt.authDetails?.userId?.slice(0, 8) ?? 'admin',
          ip: evt.authDetails?.ipAddress ?? '',
          detail: `${evt.resourceType}: ${evt.resourcePath}`,
          source: 'admin',
        });
      }

      entries.sort((a, b) => b.time - a.time);
      setEvents(entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredEvents = typeFilter === 'all'
    ? events
    : events.filter(e => e.type === typeFilter || e.source === typeFilter);

  const eventTypes = [...new Set(events.map(e => e.type))];

  function formatTime(timestamp: number): string {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  function exportCsv(): void {
    const header = 'Time,Type,User,IP,Detail,Source\n';
    const rows = filteredEvents.map(e =>
      `"${formatTime(e.time)}","${e.type}","${e.user}","${e.ip}","${e.detail}","${e.source}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vertex-audit-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const typeColor: Record<string, string> = {
    'LOGIN': '#4ADE80',
    'LOGIN_ERROR': '#EF4444',
    'LOGOUT': '#FBBF24',
    'REGISTER': COLORS.accent.blue,
    'CREATE': COLORS.accent.cyan,
    'UPDATE': COLORS.accent.gold,
    'DELETE': '#EF4444',
  };

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          Audit Log
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{
              height: '34px', padding: '0 12px', borderRadius: '6px',
              border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
              color: colors.text, fontFamily: FONTS.body, fontSize: '12px', outline: 'none',
            }}
          >
            <option value="all">All Events</option>
            <option value="login">Login Events</option>
            <option value="admin">Admin Events</option>
            {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={exportCsv} style={{
            height: '34px', padding: '0 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent', color: colors.textSecondary, fontFamily: FONTS.heading,
            fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
          }}>Export CSV</button>
          <button onClick={loadData} style={{
            height: '34px', padding: '0 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent', color: colors.textSecondary, fontFamily: FONTS.heading,
            fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
          }}>Refresh</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: isDark ? '#3b1c1c' : '#fde8e8', border: '1px solid #EF4444', borderRadius: '8px', color: '#EF4444', fontFamily: FONTS.body, fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading audit log...</p>
      ) : (
        <div style={{ backgroundColor: colors.card, borderRadius: '12px', boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight, overflow: 'hidden' }}>
          <div className="vx-desktop-header" style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr 3fr', gap: '12px', padding: '12px 20px', borderBottom: `1px solid ${colors.border}`, fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: colors.textSecondary }}>
            <span>Time</span>
            <span>Type</span>
            <span>User</span>
            <span>IP</span>
            <span>Detail</span>
          </div>

          {filteredEvents.slice(0, 100).map((evt, i) => (
            <div key={i} className="vx-stack-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr 3fr', gap: '12px', padding: '10px 20px', borderBottom: `1px solid ${colors.border}`, alignItems: 'center' }}>
              <span className="vx-labeled-cell" data-label="Time" style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>
                {formatTime(evt.time)}
              </span>
              <span
                className="vx-labeled-cell"
                data-label="Type"
                style={{
                fontFamily: FONTS.body, fontSize: '11px',
                color: typeColor[evt.type] ?? colors.text,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {evt.type}
              </span>
              <span className="vx-labeled-cell" data-label="User" style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.text }}>
                {evt.user}
              </span>
              <span className="vx-labeled-cell" data-label="IP" style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
                {evt.ip}
              </span>
              <span className="vx-event-detail vx-labeled-cell" data-label="Detail" style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {evt.detail}
              </span>
            </div>
          ))}

          {filteredEvents.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center', fontFamily: FONTS.body, fontSize: '14px', color: colors.textSecondary }}>
              No events found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
